import type { AuditResult, AuditOptions } from '@/types/audit';

class APIError extends Error {
  constructor(message: string, public status: number) {
    super(message);
    this.name = 'APIError';
  }
}

export class AuditService {
  private baseURL: string;
  private cache = new Map<string, { data: AuditResult; timestamp: number }>();
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  private readonly RUNTIME_API_KEY_STORAGE_KEY = 'seo_audit_api_key';
  private readonly DEV_PLACEHOLDER_KEY = 'development-testing-key';

  constructor() {
    // Environment-based URL configuration
    this.baseURL = import.meta.env.VITE_BACKEND_URL ||
      (import.meta.env.MODE === 'development'
        ? 'http://localhost:8080'
        : 'https://seo-audit-backend-atto5yn5oq-uc.a.run.app'
      );
    
    console.log('🔗 Backend URL:', this.baseURL);
  }

  /**
   * Persist a runtime API key (helps local dev vs production backends).
   */
  setRuntimeApiKey(key?: string) {
    if (typeof window === 'undefined') return;
    if (key && key.trim().length > 0) {
      window.localStorage.setItem(this.RUNTIME_API_KEY_STORAGE_KEY, key.trim());
    } else {
      window.localStorage.removeItem(this.RUNTIME_API_KEY_STORAGE_KEY);
    }
  }

  private getRuntimeApiKey(): string | undefined {
    if (typeof window === 'undefined') return undefined;
    const stored = window.localStorage.getItem(this.RUNTIME_API_KEY_STORAGE_KEY);
    return stored && stored.trim().length > 0 ? stored.trim() : undefined;
  }

  private backendRequiresApiKey(): boolean {
    if (!this.baseURL) return false;
    const urlLower = this.baseURL.toLowerCase();
    const isLocal = urlLower.includes('localhost') || urlLower.startsWith('http://127.');
    return !isLocal;
  }

  private getApiKey(): string | undefined {
    const envKey = import.meta.env.VITE_API_KEY?.trim();
    const prodKey = import.meta.env.VITE_PROD_API_KEY?.trim() || import.meta.env.VITE_BACKEND_API_KEY?.trim();
    const runtimeKey = this.getRuntimeApiKey();

    const candidates: Array<string | undefined> = [];

    if (runtimeKey) {
      candidates.push(runtimeKey);
    }

    if (this.backendRequiresApiKey()) {
      if (prodKey && prodKey.length > 0) {
        candidates.push(prodKey);
      }
      if (envKey && envKey.length > 0 && envKey !== this.DEV_PLACEHOLDER_KEY) {
        candidates.push(envKey);
      }
    } else if (envKey && envKey.length > 0) {
      candidates.push(envKey);
    }

    const resolved = candidates.find(Boolean);

    if (!resolved && this.backendRequiresApiKey()) {
      console.warn('⚠️ API key required for remote backend but none configured. Set VITE_PROD_API_KEY or store a key via setRuntimeApiKey().');
    }

    return resolved;
  }

  private buildJsonHeaders(extra: Record<string, string> = {}, includeContentType = true) {
    const headers: Record<string, string> = {
      ...(includeContentType ? { 'Content-Type': 'application/json' } : {}),
      ...extra
    };

    const apiKey = this.getApiKey();
    if (apiKey) {
      headers['X-API-Key'] = apiKey;
    }

    return headers;
  }

  /**
   * Perform audit using synchronous API (Sprint 4 - Direct Response)
   */
  async performAudit(url: string, options: AuditOptions = {}): Promise<AuditResult> {
    const cacheKey = `audit_${url}_${JSON.stringify(options)}`;

    // Check cache first
    const cached = this.cache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      return cached.data;
    }

    try {
      console.log('🚀 Starting direct audit for:', url);
      console.log('📋 Request options:', { fastMode: options.fastMode, fullAnalysis: options.fullAnalysis });

      const timeout = options.fastMode ? 30000 : options.fullAnalysis ? 400000 : 300000;
      console.log(`⏰ Setting timeout to ${timeout/1000}s`);

      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        console.log('⏰ Request timeout reached, aborting...');
        controller.abort();
      }, timeout);

      const requestBody = {
        url,
        enableJS: options.fullAnalysis, // Enable browserless.io for full analysis
        fastMode: options.fastMode
      };
      console.log('📤 Sending request:', requestBody);

      const startTime = Date.now();

      const response = await fetch(`${this.baseURL}/api/audit`, {
        method: 'POST',
        headers: this.buildJsonHeaders(),
        body: JSON.stringify(requestBody),
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      const requestTime = Date.now() - startTime;
      console.log(`📥 Response received in ${requestTime}ms, status: ${response.status}`);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new APIError(
          errorData.error || `HTTP ${response.status}: ${response.statusText}`,
          response.status
        );
      }

      const result = await response.json();

      // Transform to expected format if needed
      const transformedResult = this.transformToLegacyFormat(result);

      // Cache the result
      this.cache.set(cacheKey, { data: transformedResult, timestamp: Date.now() });

      return transformedResult;

    } catch (error) {
      if (error instanceof APIError) {
        throw error;
      }

      if (error instanceof Error && error.name === 'AbortError') {
        const timeoutSeconds = options.fastMode ? 30 : options.fullAnalysis ? 130 : 70;
        throw new APIError(`Audit timed out after ${timeoutSeconds} seconds. The site may be JavaScript-heavy or slow to respond. Try a different page or check if the site blocks crawlers.`, 408);
      }

      // Handle network errors
      throw new APIError(
        error instanceof Error ? error.message : 'Network error occurred',
        0
      );
    }
  }

  /**
   * Enhanced audit with heavy dependencies - preserve your lazy loading logic
   */
  async performEnhancedAudit(url: string, options: AuditOptions = {}): Promise<AuditResult> {
    try {
      const response = await fetch(`${this.baseURL}/api/audit/enhanced`, {
        method: 'POST',
        headers: this.buildJsonHeaders(),
        body: JSON.stringify({ url, options }),
      });

      if (!response.ok) {
        // Fallback to lightweight audit if enhanced fails
        console.warn('Enhanced audit failed, falling back to lightweight mode');
        return this.performAudit(url, options);
      }

      return await response.json();
    } catch (error) {
      // Graceful fallback - preserve your error handling approach
      console.warn('Enhanced audit error, falling back to lightweight mode:', error);
      return this.performAudit(url, options);
    }
  }

  /**
   * Sitemap discovery - find URLs in sitemap
   */
  async discoverSitemapUrls(url: string, maxUrls = 20): Promise<{
    success: boolean;
    baseUrl: string;
    sitemaps: Array<{ url: string; status: string; type?: string }>;
    discoveredUrls: number;
    urls: string[];
    maxUrls: number;
    executionTime: number;
    timestamp: string;
    nextStep: string;
  }> {
    try {
      // Add timeout controller for sitemap discovery
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
      
      const response = await fetch(`${this.baseURL}/api/sitemap-audit`, {
        method: 'POST',
        headers: this.buildJsonHeaders(),
        body: JSON.stringify({ url, maxUrls, mode: 'discover' }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new APIError(
          errorData.error || `Sitemap discovery failed: ${response.statusText}`,
          response.status
        );
      }

      return await response.json();
    } catch (error) {
      if (error instanceof APIError) {
        throw error;
      }
      if (error instanceof Error && error.name === 'AbortError') {
        throw new APIError('Sitemap discovery timed out (30 seconds). The site may have a large or complex sitemap.', 408);
      }
      throw new APIError('Failed to discover sitemap URLs', 0);
    }
  }

  /**
   * Batch audit - audit multiple URLs from sitemap
   */
  async performBatchAudit(url: string, maxUrls = 20): Promise<{
    success: boolean;
    baseUrl: string;
    sitemaps: Array<{ url: string; status: string; type?: string }>;
    totalUrls: number;
    maxUrls: number;
    results: Array<{
      url: string;
      success: boolean;
      executionTime: number;
      scores?: {
        seo: number;
        performance: number;
        accessibility: number;
        overall: number;
      };
      keyFindings?: string[];
      error?: string;
    }>;
    summary: {
      completed: number;
      failed: number;
      avgScore: number;
      totalExecutionTime: number;
    };
    timestamp: string;
    executionTime: number;
  }> {
    try {
      // Add longer timeout for batch processing (multiple URLs)
      const controller = new AbortController();
      const timeoutMs = Math.max(60000, maxUrls * 3000); // At least 60s, or 3s per URL
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
      
      const response = await fetch(`${this.baseURL}/api/sitemap-audit`, {
        method: 'POST',
        headers: this.buildJsonHeaders(),
        body: JSON.stringify({ url, maxUrls, mode: 'batch' }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new APIError(
          errorData.error || `Batch audit failed: ${response.statusText}`,
          response.status
        );
      }

      return await response.json();
    } catch (error) {
      if (error instanceof APIError) {
        throw error;
      }
      if (error instanceof Error && error.name === 'AbortError') {
        const timeoutSeconds = Math.max(60, maxUrls * 3);
        throw new APIError(`Batch audit timed out after ${timeoutSeconds} seconds. Try reducing the number of URLs or the site may have complex pages that take longer to analyze.`, 408);
      }
      throw new APIError('Failed to perform batch audit', 0);
    }
  }

  /**
   * Normalize PSI data into the frontend-friendly PSIResult shape.
   */
  private normalizePsiResult(raw: any, fallbackUrl: string): any | null {
    if (!raw) {
      return null;
    }

    const ensureVital = (value: any): 'good' | 'needs-improvement' | 'poor' => {
      if (value === 'good' || value === 'needs-improvement' || value === 'poor') {
        return value;
      }
      return 'needs-improvement';
    };

    const formatMetric = (value: any): string => {
      if (value === null || value === undefined) {
        return 'N/A';
      }
      if (typeof value === 'number') {
        // Assume milliseconds and convert to seconds for readability when large
        return value >= 1000 ? `${(value / 1000).toFixed(2)} s` : `${value.toFixed(0)} ms`;
      }
      const stringValue = String(value);
      return stringValue.trim().length > 0 ? stringValue : 'N/A';
    };

    const coerceScore = (value: any): number => {
      if (typeof value === 'number' && !Number.isNaN(value)) {
        return value;
      }
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    };

    const structuredPerformance = raw.performance && raw.performance.metrics;

    const performance = structuredPerformance ? {
      score: coerceScore(raw.performance.score),
      metrics: {
        fcp: formatMetric(raw.performance.metrics?.fcp),
        lcp: formatMetric(raw.performance.metrics?.lcp),
        inp: formatMetric(raw.performance.metrics?.inp ?? raw.performance.metrics?.fid),
        cls: formatMetric(raw.performance.metrics?.cls),
        speedIndex: formatMetric(raw.performance.metrics?.speedIndex),
        tbt: formatMetric(raw.performance.metrics?.tbt)
      },
      coreWebVitals: {
        fcp: ensureVital(raw.performance.coreWebVitals?.fcp),
        lcp: ensureVital(raw.performance.coreWebVitals?.lcp),
        cls: ensureVital(raw.performance.coreWebVitals?.cls),
        inp: ensureVital(raw.performance.coreWebVitals?.inp ?? raw.performance.coreWebVitals?.fid)
      }
    } : {
      score: coerceScore(raw.score),
      metrics: {
        fcp: formatMetric(raw.fcp),
        lcp: formatMetric(raw.lcp),
        inp: formatMetric(raw.inp ?? raw.fid),
        cls: formatMetric(raw.cls),
        speedIndex: formatMetric(raw.speedIndex ?? raw.speed_index),
        tbt: formatMetric(raw.tbt)
      },
      coreWebVitals: {
        fcp: ensureVital(raw.coreWebVitals?.fcp),
        lcp: ensureVital(raw.coreWebVitals?.lcp),
        cls: ensureVital(raw.coreWebVitals?.cls),
        inp: ensureVital(raw.coreWebVitals?.inp ?? raw.coreWebVitals?.fid)
      }
    };

    return {
      url: raw.url || fallbackUrl,
      timestamp: raw.timestamp || new Date().toISOString(),
      cached: Boolean(raw.cached),
      performance,
      seo: raw.seo,
      accessibility: raw.accessibility,
      opportunities: raw.opportunities || [],
      environment: raw.environment || {}
    };
  }

  /**
   * Transform Sprint 3 API response to legacy format for compatibility
   */
  private transformToLegacyFormat(result: any): AuditResult {
    if (!result || typeof result !== 'object') {
      throw new APIError('Invalid audit result format', 500);
    }

    // Map Sprint 3 format to legacy AuditResult format
    const psiMetrics = this.normalizePsiResult(result.psiMetrics, result.url || '');

    return {
      url: result.url || '',
      timestamp: result.timestamp || new Date().toISOString(),

      // Performance metrics (from PSI or performance analysis)
      psiMetrics: psiMetrics,

      // Tests structure according to AuditResult interface
      tests: {
        seo: result.tests?.seo || result.seo,
        performance: result.tests?.performance || result.performance || (psiMetrics ? { ...psiMetrics.performance, psi: psiMetrics.performance } : undefined),
        accessibility: result.tests?.accessibility || result.accessibility,
        schema: result.tests?.schema || result.schema,
        aeo: {
          ...(result.tests?.aeo || result.aeo || result.aiOptimization || {}),
          // Ensure recommendations are available at the top level
          recommendations: (result.tests?.aeo?.recommendations || result.aeo?.recommendations || result.aiOptimization?.recommendations || [])
        },
        eat: result.tests?.eat || result.eat,
        files: result.tests?.files || result.files
      },

      // Overall score calculation according to interface
      overallScore: Math.round(
        ((result.tests?.seo?.score || result.seo?.score || 0) +
         (result.tests?.performance?.score || result.performance?.score || psiMetrics?.performance?.score || 0)) / 2
      ),

      // Execution metadata according to interface
      executionTime: result.executionTime || 0,
      mode: result.mode || 'enhanced'
    };
  }

  /**
   * Clear cache - preserve your cache management
   */
  async clearCache(): Promise<void> {
    try {
      // Clear local cache
      this.cache.clear();
      
      // Clear server cache
      const response = await fetch(`${this.baseURL}/api/cache/clear`, {
        method: 'POST',
        headers: this.buildJsonHeaders()
      });

      if (!response.ok) {
        throw new APIError('Failed to clear server cache', response.status);
      }
    } catch (error) {
      console.warn('Cache clear warning:', error);
      // Don't throw - cache clearing shouldn't break the app
    }
  }

  /**
   * Generate LLMS.txt - preserve your existing logic
   */
  async generateLLMSTxt(url: string): Promise<{
    content: string;
    url: string;
    instructions: string;
  }> {
    try {
      const response = await fetch(`${this.baseURL}/api/llms/generate`, {
        method: 'POST',
        headers: this.buildJsonHeaders(),
        body: JSON.stringify({ url }),
      });

      if (!response.ok) {
        throw new APIError('LLMS.txt generation failed', response.status);
      }

      return await response.json();
    } catch (error) {
      if (error instanceof APIError) {
        throw error;
      }
      throw new APIError('Failed to generate LLMS.txt', 0);
    }
  }

  /**
   * Check server health - preserve your health check logic
   */
  async checkHealth(): Promise<{
    status: string;
    timestamp: string;
    uptime: number;
    mode: string;
    memory: {
      used: string;
      total: string;
    };
  }> {
    try {
      const response = await fetch(`${this.baseURL}/api/health`, {
        headers: this.buildJsonHeaders({}, false)
      });
      
      if (!response.ok) {
        throw new APIError('Health check failed', response.status);
      }

      return await response.json();
    } catch (error) {
      if (error instanceof APIError) {
        throw error;
      }
      throw new APIError('Unable to reach server', 0);
    }
  }

  /**
   * Get cache size for debugging
   */
  getCacheSize(): number {
    return this.cache.size;
  }

  /**
   * Get current backend URL for debugging
   */
  getBackendURL(): string {
    return this.baseURL;
  }
}

// Export singleton instance
export const auditService = new AuditService();
